/**
 * Utilities for module-level grading calculations
 *
 * Grade computation is delegated to the backend via POST /grade-preview.
 * The local aggregation helpers (aggregateTaskGrades) are kept for the
 * tooltip breakdown display only.
 */

import { calculateTaskGrade } from './gradingConversions';
import { taskRequiresEvaluation } from '../../../common/utils/taskEvaluationUtils';
import { previewModuleGrade } from '../../../common/api/endpoints/evaluations/evaluations';
import { isOralExamTask } from './evaluationFormMapping';

/**
 * Returns the grading category id for a task.
 * Prefers the canonical evaluation.gradingCategoryId when available (set by the backend
 * based on grading_category_id and mode). Falls back to deriving from gradingSystem for
 * backward compat with tasks that haven't been re-serialized yet.
 * @param {Object} task
 * @returns {string}
 */
const getGradingCategoryId = (task) => {
  const fromEval = task.evaluation?.gradingCategoryId;
  if (fromEval) return fromEval;
  // Legacy fallback
  if (task.gradingSystem === 'full_evaluation') return 'transfer';
  if (task.gradingSystem === 'pass_fail') return 'regular';
  return task.gradingSystem ?? 'regular';
};

/**
 * Aggregates and averages task grades from an array of task evaluations.
 * Used only for the grade breakdown tooltip display.
 *
 * @param {Array} taskEvaluations
 * @param {Object} levelWeightsMap
 * @param {Object} [dimensionMaxWeights={}]
 * @returns {number}
 */
export const aggregateTaskGrades = (
  taskEvaluations,
  levelWeightsMap = {},
  dimensionMaxWeights = {},
  gradeProjector = undefined,
) => {
  if (!taskEvaluations || taskEvaluations.length === 0 || !levelWeightsMap) {
    return NaN;
  }

  const taskGrades = taskEvaluations
    .map((taskEval) => {
      try {
        return calculateTaskGrade(taskEval, levelWeightsMap, dimensionMaxWeights, gradeProjector);
      } catch {
        return NaN;
      }
    })
    .filter((grade) => !Number.isNaN(grade));

  if (taskGrades.length === 0) {
    return NaN;
  }

  return parseFloat((taskGrades.reduce((sum, g) => sum + g, 0) / taskGrades.length).toFixed(1));
};

/**
 * Computes the final module grade by calling the backend grade-preview endpoint.
 *
 * @param {Object} params
 * @param {Array}    params.tasks
 * @param {Object}   params.formData - taskEvaluations keyed by task id
 * @param {Function} params.setValue - react-hook-form setter
 * @param {Function} params.showDialog
 * @param {Function} params.showToastMessage
 * @param {Array}    [params.eventKinds=[]]
 * @returns {Promise<{ ok: boolean, grade?: string }>}
 */
export const handleComputeGrade = async ({
  tasks,
  formData,
  setValue,
  showDialog,
  showToastMessage,
  eventKinds = [],
  moduleSubmissionId,
}) => {
  const gradedTasks = tasks.filter((task) => taskRequiresEvaluation(task, eventKinds));

  const taskInputs = gradedTasks
    .map((task) => {
      const taskFormData = formData[task.id];
      const gradingCategoryId = getGradingCategoryId(task);

      if (isOralExamTask(task)) {
        // Oral exam: pass sub-evaluations so the backend can compute the grade
        // using calculate_oral_exam_grade (drop-worst aggregation).
        const subEvals = taskFormData?.subEvaluations ?? {};
        const subEvaluations = Object.entries(subEvals)
          .map(([id, sub]) => ({
            id,
            dimGrades: Object.fromEntries(
              Object.entries(sub?.dimGrades ?? {})
                .filter(([, rating]) => rating?.level && rating.level !== 'not_applicable')
                .map(([dim, rating]) => [dim, rating.level]),
            ),
          }))
          .filter((se) => Object.keys(se.dimGrades).length > 0);
        if (subEvaluations.length === 0) return null;
        return {
          taskId: task.id, gradingCategoryId, dimGrades: {}, subEvaluations,
        };
      }

      // Regular essay task: pass dim-level grades.
      const evaluation = taskFormData?.evaluation?.dimensions || {};
      const dimGrades = Object.fromEntries(
        Object.entries(evaluation).map(([dim, val]) => [dim, val?.level ?? null]),
      );
      const hasAnyGrade = Object.values(dimGrades).some((v) => v !== null);
      if (!hasAnyGrade) return null;
      return { taskId: task.id, gradingCategoryId, dimGrades };
    })
    .filter(Boolean);

  // No evaluated tasks yet — a normal empty state, not an error.
  if (taskInputs.length === 0) {
    return { ok: false };
  }

  try {
    const previewPayload = { taskInputs };
    if (moduleSubmissionId) {
      previewPayload.moduleSubmissionId = moduleSubmissionId;
    }
    const response = await previewModuleGrade(previewPayload);
    const result = response.data?.data;

    if (!result || result.grade === null || result.grade === undefined) {
      showToastMessage('error', 'Could not compute grade. Check that tasks are fully evaluated.', 'Grading');
      return { ok: false };
    }

    const formattedGrade = Number(result.grade).toFixed(1);

    // Show warning dialog when not all tasks are evaluated (skip for auto-calc no-op)
    const isAutoCalculation = showDialog.toString().includes('{}') || showDialog.name === '';
    const hasPartialEval = taskInputs.length < gradedTasks.length;

    if (!isAutoCalculation && hasPartialEval) {
      showDialog({
        title: 'Incomplete Evaluation',
        message: `The grade would be ${formattedGrade}, but not all tasks are evaluated. Continue anyway?`,
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Use Anyway',
            onPress: () => setValue('grading.grade', formattedGrade, { shouldValidate: true, shouldDirty: true }),
          },
        ],
      });
      return { ok: false };
    }

    setValue('grading.grade', formattedGrade, { shouldValidate: true, shouldDirty: true });

    const breakdown = result.categories
      .filter((c) => c.grade !== null)
      .map((c) => `${Number(c.grade).toFixed(1)} ${c.label}`)
      .join(' / ');

    showToastMessage('success', `Module grade computed${breakdown ? ` (${breakdown})` : ''}: ${formattedGrade}`, 'Grading');
    return { ok: true, grade: formattedGrade };
  } catch (error) {
    showToastMessage('error', `Error computing grade: ${error.message}`, 'Grading Error');
    return { ok: false };
  }
};

/**
 * Calculates a grade from numerical scores
 * @param {Object} scores - Score object with dimension keys and scores
 * @param {Object} scoringSchema - Schema defining max scores for dimensions
 * @returns {string} The calculated grade as a string
 */
export const calculateGradeFromScores = (scores, scoringSchema) => {
  if (!scores) return null;

  // compute a map key -> max score
  const maxScores = Object.fromEntries(
    scoringSchema.regularTask.concat(scoringSchema.transferTask).map((dim) => [dim.key, dim.max || 10]),
  );

  // Calculate deviations from max for each dimension
  const deviations = Object.entries(scores).map(([key, score]) => (maxScores[key] - score) * 0.1);

  // total deviation
  const totalDeviation = deviations.reduce((sum, dev) => sum + dev, 0);
  // Calculate grade (1.0 plus deviation, capped at 4.0 or 5.0)
  const grade = Math.min(4.0, 1.0 + totalDeviation);
  return grade >= 4.0 ? '5.0' : String(grade.toFixed(1));
};
